/**
 * RawMaterialIssueModal
 * Modal for reporting damaged raw material found during production.
 * Supports GS1 barcode scanning (auto-extracts weight) and manual weight entry.
 * Shows impact preview (before/after weights and expected boxes).
 * Lists previous material issues for the sub-session.
 * Persists state to sessionStorage so page refresh doesn't lose progress.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { X, AlertTriangle, ScanLine, Keyboard, ChevronDown, ChevronUp } from 'lucide-react';
import { useBarcodeScanner } from '../../../hooks/useBarcodeScanner';
import { parseGS1128Barcode } from '../../../utils/barcodeUtils';
import operatorService, { ProductionSession } from '../../../services/operatorService';
import {
  saveMaterialIssueState,
  loadMaterialIssueState,
  clearMaterialIssueState,
} from '../../../utils/mobileSessionStorage';

interface RawMaterialIssueModalProps {
  isOpen: boolean;
  session: ProductionSession;
  operatorEmployeeNumber: string;
  operatorName: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface MaterialIssueHistoryItem {
  id: number;
  created_at: string;
  changed_by: string;
  reason: string;
  damaged_weight: number;
  weight_source: string;
  item_number: string;
  transfer_number: string;
  weight_before: number;
  weight_after: number;
  target_before: number;
  target_after: number;
}

const RawMaterialIssueModal: React.FC<RawMaterialIssueModalProps> = ({
  isOpen,
  session,
  operatorEmployeeNumber,
  operatorName,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();

  // Form state
  const [damagedWeight, setDamagedWeight] = useState<string>('');
  const [reason, setReason] = useState('');
  const [barcodeRaw, setBarcodeRaw] = useState<string | null>(null);
  const [weightSource, setWeightSource] = useState<'barcode' | 'manual'>('manual');
  const [barcodeInfo, setBarcodeInfo] = useState<{ gtin?: string; weight?: number; lot?: string } | null>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [entryMode, setEntryMode] = useState<'scan' | 'manual'>('scan');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<MaterialIssueHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);
  const weightInputRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLSelectElement>(null);
  const initializedRef = useRef(false);

  // Barcode scanner hook
  const { clearLastScanned } = useBarcodeScanner({
    onScan: (barcode) => {
      handleBarcodeScan(barcode);
      setScanInput('');
    },
    enabled: isOpen && entryMode === 'scan',
  });

  // Load history when modal opens
  useEffect(() => {
    if (isOpen && session.sub_session_number) {
      loadHistory();
    }
  }, [isOpen, session.sub_session_number]);

  // Restore persisted state on first open, or reset on fresh open
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      initializedRef.current = true;
      const saved = loadMaterialIssueState();
      if (saved && saved.isOpen) {
        // Restore previous progress
        setEntryMode(saved.entryMode);
        setDamagedWeight(saved.damagedWeight);
        setReason(saved.reason);
        setBarcodeRaw(saved.barcodeRaw);
        setWeightSource(saved.weightSource);
        setBarcodeInfo(saved.barcodeInfo);
        setError(null);
        setSuccessMessage(null);
        setScanInput('');
        // Focus the appropriate input
        setTimeout(() => {
          if (saved.entryMode === 'scan') scanInputRef.current?.focus();
          else weightInputRef.current?.focus();
        }, 100);
      } else {
        // Fresh open — reset everything
        setDamagedWeight('');
        setReason('');
        setBarcodeRaw(null);
        setBarcodeInfo(null);
        setWeightSource('manual');
        setError(null);
        setSuccessMessage(null);
        setEntryMode('scan');
        setScanInput('');
        clearLastScanned();
        setTimeout(() => scanInputRef.current?.focus(), 100);
      }
    }
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen]);

  // Persist form state to sessionStorage whenever it changes
  useEffect(() => {
    if (isOpen) {
      saveMaterialIssueState({
        isOpen: true,
        entryMode,
        damagedWeight,
        reason,
        barcodeRaw,
        weightSource,
        barcodeInfo,
      });
    }
  }, [isOpen, entryMode, damagedWeight, reason, barcodeRaw, weightSource, barcodeInfo]);

  async function loadHistory() {
    if (!session.sub_session_number) return;
    setLoadingHistory(true);
    try {
      const data = await operatorService.getMaterialIssues(session.sub_session_number);
      setHistory(data.issues || []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }

  function handleBarcodeScan(barcode: string) {
    setError(null);
    setSuccessMessage(null);

    const parsed = parseGS1128Barcode(barcode);
    if (!parsed || !parsed.gtin) {
      setError(t('materialIssue.barcodeNotMatched'));
      return;
    }

    setBarcodeRaw(barcode);
    setBarcodeInfo(parsed);
    setWeightSource('barcode');

    if (parsed.normalizedWeightLb) {
      setDamagedWeight(parsed.normalizedWeightLb.toString());
    }

    // Focus reason input after successful scan
    setTimeout(() => reasonRef.current?.focus(), 50);
  }

  function handleClose() {
    clearMaterialIssueState();
    onClose();
  }

  const handleSubmit = useCallback(async () => {
    if (!session.sub_session_number) return;

    const weight = parseFloat(damagedWeight);
    if (!weight || weight <= 0) {
      setError(t('materialIssue.weightExceedsAvailable'));
      return;
    }
    if (weight > (parseFloat(String(session.initial_material_weight)) || 0)) {
      setError(t('materialIssue.weightExceedsAvailable'));
      return;
    }
    if (!reason.trim()) {
      setError(t('materialIssue.reasonPlaceholder'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await operatorService.reportMaterialIssue({
        sub_session_number: session.sub_session_number,
        damaged_weight: weight,
        reason: reason.trim(),
        barcode_raw: barcodeRaw || undefined,
        weight_source: weightSource,
        operator_employee_number: operatorEmployeeNumber,
        operator_name: operatorName,
      });

      setSuccessMessage(t('materialIssue.success'));
      await loadHistory();
      // Clear form
      setDamagedWeight('');
      setReason('');
      setBarcodeRaw(null);
      setBarcodeInfo(null);
      setWeightSource('manual');
      clearMaterialIssueState();

      onSuccess();

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      const message = err?.response?.data?.error || t('materialIssue.error');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [session, damagedWeight, reason, barcodeRaw, weightSource, operatorEmployeeNumber, operatorName, onSuccess, onClose, t]);

  if (!isOpen) return null;

  // Impact preview calculations — parseFloat guards against string values from session
  const weight = parseFloat(damagedWeight) || 0;
  const currentInitial = parseFloat(String(session.initial_material_weight)) || 0;
  const currentTarget = parseFloat(String(session.target_output_weight)) || 0;
  const unitWeight = parseFloat(String(session.box_target_weight)) || 1;
  const yieldRatio = currentInitial > 0 ? currentTarget / currentInitial : 0;
  const newInitial = currentInitial - weight;
  const newTarget = newInitial * yieldRatio;
  const currentExpectedBoxes = Math.floor(currentTarget / unitWeight);
  const newExpectedBoxes = weight > 0 ? Math.floor(newTarget / unitWeight) : currentExpectedBoxes;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('materialIssue.title')}
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('materialIssue.description')}
          </p>

          {/* Entry mode toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setEntryMode('scan');
                setTimeout(() => scanInputRef.current?.focus(), 50);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                entryMode === 'scan'
                  ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                  : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400'
              }`}
            >
              <ScanLine className="w-4 h-4" />
              {t('materialIssue.scanBarcode')}
            </button>
            <button
              onClick={() => {
                setEntryMode('manual');
                setTimeout(() => weightInputRef.current?.focus(), 50);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                entryMode === 'manual'
                  ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                  : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400'
              }`}
            >
              <Keyboard className="w-4 h-4" />
              {t('materialIssue.manualWeight')}
            </button>
          </div>

          {/* Barcode scan input — only in scan mode */}
          {entryMode === 'scan' && (
            <div>
              {barcodeInfo ? (
                <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                    <ScanLine className="w-4 h-4" />
                    <span className="font-medium">{session.raw_material_name}</span>
                    {barcodeInfo.weight && (
                      <span className="font-semibold">— {barcodeInfo.weight} {t('materialIssue.lbs')}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBarcodeInfo(null);
                      setBarcodeRaw(null);
                      setDamagedWeight('');
                      setWeightSource('manual');
                      setError(null);
                      setTimeout(() => scanInputRef.current?.focus(), 50);
                    }}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <input
                  ref={scanInputRef}
                  type="text"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && scanInput.trim()) {
                      e.preventDefault();
                      handleBarcodeScan(scanInput.trim());
                      setScanInput('');
                    }
                  }}
                  placeholder="Scan barcode"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg"
                />
              )}
            </div>
          )}

          {/* Weight input — only in manual mode */}
          {entryMode === 'manual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('materialIssue.weightLabel')}
              </label>
              <input
                ref={weightInputRef}
                type="text"
                inputMode="decimal"
                value={damagedWeight}
                onChange={(e) => {
                  // Allow only numbers and decimal point
                  const val = e.target.value;
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setDamagedWeight(val);
                    setWeightSource('manual');
                    setError(null);
                  }
                }}
                placeholder={t('materialIssue.weightPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    reasonRef.current?.focus();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg"
              />
            </div>
          )}

          {/* Reason dropdown — required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('materialIssue.reasonLabel')} <span className="text-red-500">*</span>
            </label>
            <select
              ref={reasonRef}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="" disabled>{t('materialIssue.reasonSelectPlaceholder')}</option>
              <option value="Wrong material">{t('materialIssue.reasonWrongMaterial')}</option>
              <option value="Wrong temperature">{t('materialIssue.reasonWrongTemperature')}</option>
              <option value="Material damaged">{t('materialIssue.reasonMaterialDamaged')}</option>
              <option value="Material expired">{t('materialIssue.reasonMaterialExpired')}</option>
            </select>
          </div>

          {/* Impact preview — hidden for now, kept for future use */}
          {false && weight > 0 && weight <= currentInitial && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-2">
                {t('materialIssue.impactPreview')}
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{t('materialIssue.currentInputWeight')}:</span>
                  <span className="ml-1 font-medium text-gray-900 dark:text-white">{currentInitial.toFixed(2)} {t('materialIssue.lbs')}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{t('materialIssue.newInputWeight')}:</span>
                  <span className="ml-1 font-medium text-orange-700 dark:text-orange-300">{newInitial.toFixed(2)} {t('materialIssue.lbs')}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{t('materialIssue.currentTargetOutput')}:</span>
                  <span className="ml-1 font-medium text-gray-900 dark:text-white">{currentTarget.toFixed(2)} {t('materialIssue.lbs')}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{t('materialIssue.newTargetOutput')}:</span>
                  <span className="ml-1 font-medium text-orange-700 dark:text-orange-300">{newTarget.toFixed(2)} {t('materialIssue.lbs')}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{t('materialIssue.currentExpectedBoxes')}:</span>
                  <span className="ml-1 font-medium text-gray-900 dark:text-white">{currentExpectedBoxes}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">{t('materialIssue.newExpectedBoxes')}:</span>
                  <span className="ml-1 font-medium text-orange-700 dark:text-orange-300">{newExpectedBoxes}</span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Success message */}
          {successMessage && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
              {successMessage}
            </div>
          )}

          {/* Submit buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !damagedWeight || !reason.trim() || parseFloat(damagedWeight) <= 0}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSubmitting ? t('materialIssue.submitting') : 'Submit'}
            </button>
          </div>

          {/* Previous issues */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              <span>{t('materialIssue.previousIssues')} ({history.length})</span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHistory && (
              <div className="mt-2 space-y-2">
                {loadingHistory ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
                ) : history.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('materialIssue.noPreviousIssues')}</p>
                ) : (
                  history.map((item) => (
                    <div key={item.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900 dark:text-white">
                          -{item.damaged_weight} {t('materialIssue.lbs')}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">{item.reason}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawMaterialIssueModal;
